/**
 * ThemeSelector Component
 *
 * Dropdown selector for switching between theme modes.
 * Supports Light, Dark, System, and Custom themes.
 *
 * Theme-compliant: Uses CSS variables for colors.
 */

import { Monitor, Moon, Palette, Sun } from '@tonnie/assets';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@tonnie/ui';
import { useTranslation } from '@tonnie/locale';

import { useTheme, type ThemeMode } from '@/core/contexts/ThemeContext';
import { featureFlags } from '@/core/config/feature-flags';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface IThemeSelectorProps {
  /** Additional CSS classes */
  className?: string;
}

// ═══════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════

/**
 * Theme selector dropdown for the header
 *
 * @example
 * ```tsx
 * <ThemeSelector />
 * ```
 */
export function ThemeSelector({ className }: IThemeSelectorProps) {
  const { themeMode, setThemeMode } = useTheme();
  const { t } = useTranslation();

  const getIcon = () => {
    switch (themeMode) {
      case 'light':
        return <Sun className="w-5 h-5" />;
      case 'dark':
        return <Moon className="w-5 h-5" />;
      case 'custom':
        return <Palette className="w-5 h-5" />;
      case 'system':
        return <Monitor className="w-5 h-5" />;
      default:
        return <Sun className="w-5 h-5" />;
    }
  };

  const getLabel = (mode: ThemeMode) => {
    switch (mode) {
      case 'light':
        return t('theme.light');
      case 'dark':
        return t('theme.dark');
      case 'system':
        return t('theme.system');
      case 'custom':
        return t('theme.custom');
      default:
        return mode;
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className={className}>
          {getIcon()}
          <span className="sr-only">
            {t('theme.theme')}: {getLabel(themeMode)}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        <DropdownMenuItem
          onClick={() => setThemeMode('light')}
          className="flex items-center gap-2 cursor-pointer"
        >
          <Sun className="w-4 h-4" />
          <span>{getLabel('light')}</span>
          {themeMode === 'light' && <span className="ml-auto text-xs">✓</span>}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setThemeMode('dark')}
          className="flex items-center gap-2 cursor-pointer"
        >
          <Moon className="w-4 h-4" />
          <span>{getLabel('dark')}</span>
          {themeMode === 'dark' && <span className="ml-auto text-xs">✓</span>}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setThemeMode('system')}
          className="flex items-center gap-2 cursor-pointer"
        >
          <Monitor className="w-4 h-4" />
          <span>{getLabel('system')}</span>
          {themeMode === 'system' && <span className="ml-auto text-xs">✓</span>}
        </DropdownMenuItem>
        {featureFlags.ENABLE_CUSTOM_THEME && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setThemeMode('custom')}
              className="flex items-center gap-2 cursor-pointer"
            >
              <Palette className="w-4 h-4" />
              <span>{getLabel('custom')}</span>
              {themeMode === 'custom' && <span className="ml-auto text-xs">✓</span>}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ThemeSelector;
